import { useState, useRef } from 'react'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { GrafHeaderMin } from './shared/GrafHeaderMin'
import { ChartFrame, withJsonFormat } from './shared/ChartFrame'
import { GrafDataGrid } from './GrafDataGrid'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { useDayPeriodStore } from '../../store/useDayPeriodStore'
import { useTimeZoneStore } from '../../store/useTimeZoneStore'
import { useGrafStore } from '../../store/useGrafStore'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useGrafTableData, buildChartUrl } from '../../hooks/useGrafData'
import { useChartFillBust } from '../../hooks/shared/useChartFillBust'
import { useChartToFill } from '../../hooks/shared/useToFill'
import { useAuthStore } from '../../store/useAuthStore'

const CHART_BASE = import.meta.env.VITE_API_URL || '/api'
const SLUG = 'graf'

function today() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

export function GrafView({ station }) {
  const { date, setDate } = useDayPeriodStore()
  const tz = useTimeZoneStore(s => s.tz)
  const {
    windowDays, setWindowDays,
    alignEnd, setAlignEnd,
    isAktual, setIsAktual,
    activeTab, setActiveTab,
    chartFormat, interactive,
  } = useGrafStore()
  const token = useAuthStore(s => s.token)
  const orientation = useTableOrientationStore(s => s.getOrientation(SLUG))

  const { dataMap, labelMap, dayMap, isLoading, isError } = useGrafTableData(
    station?.gh_id, date, windowDays, alignEnd, isAktual, activeTab === 'data'
  )

  // Graf se vždy stahuje jako json[<fmt>,meta] (kvůli meta.toFill) — buildChartUrl bez formátu.
  const fillBust = useChartFillBust()
  const currentChartUrl = withJsonFormat(
    buildChartUrl(CHART_BASE, station?.gh_id, date, windowDays, alignEnd, isAktual, tz, /*format*/ null),
    chartFormat
  ) + fillBust
  const chartUrlRef = useRef(currentChartUrl)
  if (activeTab === 'graf') chartUrlRef.current = currentChartUrl
  const chartUrl = chartUrlRef.current

  const [chartMeta, setChartMeta] = useState(null)
  const screenKey = JSON.stringify(['graf', station?.gh_id, date, windowDays, alignEnd, isAktual, tz])
  useChartToFill(screenKey, chartMeta, { gran: '10M', live: isAktual }, activeTab === 'graf')

  function handleDayChange(newDate) {
    setDate(newDate)
    setIsAktual(false)
  }

  function handleAktual() {
    setDate(today())
    setIsAktual(true)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}

        <GrafHeaderMin
          obsType="10M"
          date={date}
          onDayChange={handleDayChange}
          isAktual={isAktual}
          onAktual={handleAktual}
          windowDays={windowDays}
          setWindowDays={setWindowDays}
          alignEnd={alignEnd}
          setAlignEnd={setAlignEnd}
          useStore={useGrafStore}
          tzTrailing={activeTab === 'data' ? <TableOrientationToggle slug={SLUG} /> : null}
        />

        {/* Záložky */}
        <div className="flex gap-2 border-b border-gray-100 -mb-4 pb-0">
          {['graf', 'data'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'graf' ? 'Graf' : 'Data'}
            </button>
          ))}
        </div>
      </div>

      {/* Obsah záložky */}
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {activeTab === 'graf' && (
          <div className="overflow-auto">
            <ChartFrame
              key={`${chartUrl}:${token ?? 'anon'}`}
              url={chartUrl}
              gran="10m"
              interactive={interactive}
              onMeta={setChartMeta}
            />
          </div>
        )}

        {activeTab === 'data' && (
          <>
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner size="sm" /> Načítání dat…
              </div>
            )}
            {isError && (
              <p className="text-red-500 text-sm">Chyba při načítání dat</p>
            )}
            {!isLoading && !isError && (
              <GrafDataGrid dataMap={dataMap} labelMap={labelMap} dayMap={dayMap} orientation={orientation} />
            )}
          </>
        )}
      </div>
    </div>
  )
}
